<template>
  <div :class="wrapperClass">
    <slot name="loading" v-if="loading"></slot>
    <table v-else class="data-table w-full">
      <thead>
        <tr>
          <th
            v-for="(col, index) in normalizedColumns"
            :key="index"
            :style="col.headerStyle"
            :class="[col.headerClass, { sorted: sortKey === col.field }]"
            class="even-row"
            @click="sortColumn(col)"
          >
            <template v-if="col.selectionMode === 'multiple'">
              <input
                type="checkbox"
                class="custom-check"
                @change="toggleAllSelection"
                :checked="isAllSelected"
              />
            </template>
            <template v-else>
              <span class="header-content"
                >{{ col.header }}
                <i
                  v-if="
                    sortKey === col.field &&
                    sortOrder == 'asc' &&
                    col.field !== 'actions' &&
                    isSort
                  "
                  :class="sortIcons.asc"
                ></i>
                <i
                  v-else-if="
                    sortKey === col.field &&
                    sortOrder === 'desc' &&
                    col.field !== 'actions' &&
                    isSort
                  "
                  :class="sortIcons.desc"
                ></i>
                <i
                  class="fa fa-sort ml-2"
                  v-else-if="col.field !== 'actions' && isSort"
                >
                </i>
              </span>
            </template>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, rowIndex) in value"
          :key="getRowKey(row)"
          :class="{
            'row-disabled': rowDisabled ? rowDisabled(row) : false,
            'clickable-row': true,
            'even-row': rowIndex % 2 === 0,
            'odd-row': rowIndex % 2 !== 0,
          }"
        >
          <td
            v-for="(col, colIndex) in normalizedColumns"
            :key="colIndex"
            :class="[
              getCellClass(col),
              { 'text-center': isNumericColumn(col.field) },
            ]"
            @click="handleCellClick(col, row, rowIndex)"
          >
            <template v-if="col.selectionMode === 'multiple'">
              <input
                type="checkbox"
                :value="row"
                class="custom-check"
                v-model="selectedRows"
                @change="updateSelection"
              />
            </template>
            <template v-else-if="$slots[col.field]">
              <slot :name="col.field" :data="row" :index="rowIndex" />
            </template>
            <template v-else-if="col.slotName && $slots[col.slotName]">
              <slot :name="col.slotName" :data="row" :index="rowIndex" />
            </template>
            <template v-else>
              <span v-if="col.linkable" class="atlink">
                {{ row[col.field] }}
              </span>
              <span v-else-if="col.field === 'actions'">
                <button
                  class="action-button"
                  @click.stop="toggleActionsDropdown(row, $event)"
                >
                  <i class="icon-shared"></i>
                </button>
                <div
                  v-if="isActionsOpen && currentRow === row"
                  class="dropdown-menu"
                >
                  <div
                    v-for="(option, index) in splitValues"
                    :key="index"
                    class="dropdown-item"
                    @click.stop="handleAction(option.action, row)"
                  >
                    <i
                      v-if="option.icon"
                      :class="option.icon"
                      style="margin-right: 8px"
                    ></i>
                    {{ option.label }}
                  </div>
                </div>
              </span>

              <span v-else-if="col.field === 'payload' && row.payload !== null">
                <i class="fa fa-eye"></i>
              </span>
              <span v-else>
                {{ row[col.field] }}
              </span>
            </template>
          </td>
        </tr>
        <tr v-if="!value || value.length === 0">
          <td :colspan="normalizedColumns.length" class="no-data">
            <slot name="empty">No data available.</slot>
          </td>
        </tr>
      </tbody>
    </table>
    <span
      v-if="validation?.$error && validation?.required?.$invalid"
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ PropName }} is required.
    </span>
  </div>
</template>
<script>
export default {
  name: "DataTableVue",
  props: {
    id: { type: String, required: true, default: "" },
    value: { type: Array, required: true },
    selection: { type: [Array, Object], default: null },
    dataKey: { type: String, default: null },
    wrapperClass: { type: String, default: "table-wrapper" },
    rowDisabled: { type: Function, default: null },
    validation: { type: Object, default: null },
    PropName: { type: String, default: "" },
    isSort: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    rowClickRoute: { type: String, default: null },
    splitValues: { type: Array, default: () => [] }, // New prop for split button options
    sortIcons: {
      type: Object,
      default: () => ({
        asc: "fa fa-sort", // Default ascending icon (e.g., Font Awesome)
        desc: "fa fa-sort", // Default descending icon
        neutral: "fa fa-sort", // Default neutral icon
      }),
    },
  },
  emits: [
    "update:selection",
    "page",
    "sort",
    "row-click",
    "action-click",
    "cell-click",
  ],
  data() {
    return {
      selectedRows: this.selection || [],
      sortKey: null, // Track the current sort column
      sortOrder: "asc", // Track the sort direction
      isActionsOpen: false, // Track dropdown visibility
      currentRow: null, // Track the current row for the dropdown
    };
  },
  computed: {
    normalizedColumns() {
      const cols =
        this.$slots
          .default?.()
          .filter((vnode) => vnode.type?.name === "Column")
          .map((vnode) => {
            const props = vnode.props || {};
            return {
              field: props.field,
              header: props.header,
              sortable: props.sortable,
              selectionMode: props.selectionMode,
              headerStyle: props.headerStyle,
              headerClass: props.headerClass,
              slotName: props.field,
              style: props.style,
              class: props.class,
              cellClass: props.cellClass,
              onCellClick: props.onCellClick,
              hasRowClick: !!props.onRowClick,
              linkable: props.linkable || false,
            };
          }) || [];
      return cols;
    },
    isAllSelected() {
      return (
        this.value.length > 0 && this.selectedRows.length === this.value.length
      );
    },
  },
  watch: {
    selectedRows(newSelection) {
      this.$emit("update:selection", newSelection);
    },
    selection(newSelection) {
      this.selectedRows = newSelection || [];
    },
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
  methods: {
    handleOutsideClick(event) {
      const dropdown = this.$el.querySelector(".dropdown-menu");
      const actionButton = this.$el.querySelector(".action-button");

      if (
        this.isActionsOpen &&
        dropdown &&
        !dropdown.contains(event.target) &&
        actionButton &&
        !actionButton.contains(event.target)
      ) {
        this.isActionsOpen = false;
        this.currentRow = null;
      }
    },
    getRowKey(row) {
      return this.dataKey ? row[this.dataKey] : row;
    },
    getCellClass(column) {
      return column.cellClass || "";
    },
    isNumericColumn(field) {
      return ["users", "sites", "deals"].includes(field);
    },
    handleCellClick(column, row, rowIndex) {
      if (column.onCellClick) {
        if (typeof column.onCellClick === "function") {
          column.onCellClick({ row, rowIndex });
        } else {
          this.$emit("cell-click", { column, row, rowIndex });
        }
        return;
      }
      if (column.hasRowClick && this.rowClickRoute && row.companyId) {
        try {
          this.$router.push({
            name: this.rowClickRoute,
            params: { id: row.companyId },
          });
          this.$emit("row-click", { row, rowIndex });
        } catch (error) {
          console.error("Navigation error:", error);
        }
      }
    },
    toggleAllSelection(event) {
      if (event.target.checked) {
        this.selectedRows = [...this.value];
      } else {
        this.selectedRows = [];
      }
    },
    updateSelection() {
      this.$emit("update:selection", this.selectedRows);
    },
    sortColumn(col) {
      // if (!col.sortable) return;

      if (this.sortKey === col.field) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = col.field;
        this.sortOrder = "asc";
      }

      this.$emit("sort", {
        field: this.sortKey,
        order: this.sortOrder,
      });
    },
    toggleActionsDropdown(row, event) {
      event.stopPropagation();
      if (this.isActionsOpen && this.currentRow === row) {
        this.isActionsOpen = false;
        this.currentRow = null;
      } else {
        this.isActionsOpen = true;
        this.currentRow = row;
      }
    },
    handleAction(action, row) {
      if (action) {
        if (typeof action === "function") {
          action(row); // Execute the function if provided
        } else {
          this.$emit("action-click", { row, action }); // Emit event with row and action
        }
      }
      this.isActionsOpen = false;
      this.currentRow = null;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Existing styles */
.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
}
.data-table {
  border-collapse: collapse;
  font-family: "Voyado Text";
  color: #21211f;
  width: 100%;
  border: 1px solid #e2e2e2;
}
th {
  background-color: #ffffff;
  border-bottom: 1px solid #e2e2e2;
  padding: 12px;
  text-align: left;
  position: sticky;
  font-weight: 600;
  color: #333;
  overflow-wrap: break-word;
  // cursor: pointer;
}

td {
  border-bottom: 1px solid #e2e2e2;
  padding: 12px;
  font-size: 14.22px;
  text-align: left;
  color: #000000;
}
.even-row {
  background-color: #fff;
}
.odd-row {
  background-color: #fff;
}
tr:hover {
  background-color: #fff;
}
.clickable-row {
  // cursor: pointer;
}
.row-disabled {
  opacity: 0.6;
  pointer-events: none;
}
.no-data {
  text-align: center;
  padding: 20px;
  color: #666;
}
.p-error {
  font-size: 14px;
  color: #dc3545;
}
td:first-child {
  padding-left: 16px;
}
.text-center {
  text-align: center;
}
.atlink {
  color: #388988;
  text-decoration: underline;
  cursor: pointer;
}
.atlink:hover {
  color: #388988;
  text-decoration: underline;
}
.actions-cell {
  text-align: center;
}
.actions-cell i {
  cursor: pointer;
  font-size: 16px;
  color: #666;
}
.actions-cell i:hover {
  color: #2d6e6d;
}
.custom-check {
  appearance: none;
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  padding: 10px;
  border: 1px solid #c0c0bc;
  border-radius: 4px;
  background: #fff;
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
  position: relative;
  // cursor: pointer;
}
.custom-check:checked {
  background-color: #43a584;
  border: 2px solid #348067;
}
.custom-check:checked::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 6px;
  width: 5px;
  height: 13px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.custom-check:hover:not(:disabled) {
  border-color: #2d6e6d;
}
.custom-check:focus {
  outline: none;
  border: 1px solid #348067;
  box-shadow: none;
}
.custom-check.p-invalid {
  border-color: #dc3545;
}
.custom-check:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}
.sort-icon {
  margin-left: 5px;
  font-size: 12px;
  color: #666;
}
.sort-icon i {
  vertical-align: middle;
}
/* New styles for the action button */
.action-button {
  height: 40px;
  padding: 16px;
  gap: 8px;
  border-radius: 4px;
  border: 2px solid var(--forest-100, #E0E9E8);
  background: #fff;
  cursor: pointer;
  font-size: 14.22px;
  font-family: "Voyado Text", sans-serif;
}
.dropdown-menu {
  position: absolute;
  // top: 0;
  right: 0;
  transform: translateX(-20px) translateY(-52%);
  background-color: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 150px;
  padding: 5px 0;
}
.dropdown-item {
  padding: 8px 12px;
  // cursor: pointer;
  color: #333;
}
.dropdown-item:hover {
  background-color: #f5f5f5;
}
.header-content {
  display: inline-flex;
  align-items: center;
  font-size: 16px;
}
::deep(.sort-icon) {
  margin-left: 0;
  vertical-align: middle;
}
</style>
